import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { AuthProvider } from '@/contexts/AuthContext'
import { PageWrapper } from '@/components/layout'
import { RequireAuth } from '@/components/RequireAuth'
import { Home, Tools, NotFound, Auth, Settings, AdminDashboard } from '@/pages'
import { Shop } from '@/pages/Shop'
import { RedirectHandler } from '@/pages/RedirectHandler'
import { TermsOfService } from '@/pages/TermsOfService'
import { VerifyEmail } from '@/pages/VerifyEmail'
import { Donate } from '@/pages/Donate'

// Tool imports
import { QRGenerator } from '@/pages/tools/QRGenerator'
import { PasswordGenerator } from '@/pages/tools/PasswordGenerator'
import { HashGenerator } from '@/pages/tools/HashGenerator'
import { Base64 } from '@/pages/tools/Base64'
import { UUIDGenerator } from '@/pages/tools/UUIDGenerator'
import { ColorPicker } from '@/pages/tools/ColorPicker'
import { Changelog } from '@/pages/Changelog'
import { TempMail } from '@/pages/tools/TempMail'
import { ImageCompressor } from '@/pages/tools/ImageCompressor'
import { LoremIpsum } from '@/pages/tools/LoremIpsum'
import { JsonFormatter } from '@/pages/tools/JsonFormatter'
import { WordCounter } from '@/pages/tools/WordCounter'
import { TextCase } from '@/pages/tools/TextCase'
import { PDFStudio } from '@/pages/tools/PDFStudio'
import { StudentSlip } from '@/pages/tools/StudentSlip'
import { ResumeBuilder } from '@/pages/tools/ResumeBuilder'
import { InvoiceGenerator } from '@/pages/tools/InvoiceGenerator'
import { ImageResizer } from '@/pages/tools/ImageResizer'
import { Timestamp } from '@/pages/tools/Timestamp'
import { MetaTags } from '@/pages/tools/MetaTags'
import { MarkdownEditor } from '@/pages/tools/MarkdownEditor'
import { CreditCardGenerator } from '@/pages/tools/CreditCardGenerator'
import { IpInfo } from '@/pages/tools/IpInfo'
import { LuhnValidator } from '@/pages/tools/LuhnValidator'
import { UrlShortener } from '@/pages/tools/UrlShortener'
import { RequestEncryptor } from '@/pages/tools/RequestEncryptor'
import { IdentityGenerator } from '@/pages/tools/IdentityGenerator'
import { CurlConverter } from '@/pages/tools/CurlConverter'
import { UnitConverter } from '@/pages/tools/UnitConverter'
import { SitemapGenerator } from '@/pages/tools/SitemapGenerator'
import { UrlEncoderDecoder } from '@/pages/tools/UrlEncoderDecoder'
import { PomodoroTimer } from '@/pages/tools/PomodoroTimer'
import { AsciiArtGenerator } from '@/pages/tools/AsciiArtGenerator'
import { TextDiff } from '@/pages/tools/TextDiff'
import { TextDiffSideBySide } from '@/pages/tools/TextDiffSideBySide'
import { StringInspector } from '@/pages/tools/StringInspector'
import { CronEditor } from '@/pages/tools/CronEditor'
import { ESignature } from '@/pages/tools/ESignature'
import { VideoDownloader } from '@/pages/tools/VideoDownloader'

// Encryptor imports
import {
  AdyenV1,
  AdyenV2,
  CybersourceV1,
  CybersourceV2,
  CardCvv,
  Vantiv,
  Clover,
  Eway,
  CardConnect,
  Paytrace,
  Zuora,
  FirstDataV2,
  SecurePay,
  Auruspay,
  Oceanpayment,
  CaptchaV3,
  Freedompay,
  Paysimple,
  Binlist,
} from '@/pages/encryptors'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              className: 'font-mono',
            }}
          />
          <Routes>
            {/* Auth - no wrapper */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Main pages */}
            <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
            <Route path="/tools" element={<PageWrapper><Tools /></PageWrapper>} />
            <Route path="/settings" element={<PageWrapper><RequireAuth><Settings /></RequireAuth></PageWrapper>} />
            <Route path="/admin" element={<PageWrapper><RequireAuth><AdminDashboard /></RequireAuth></PageWrapper>} />
            <Route path="/shop" element={<PageWrapper><Shop /></PageWrapper>} />
            <Route path="/tos" element={<PageWrapper><TermsOfService /></PageWrapper>} />
            <Route path="/donate" element={<PageWrapper><Donate /></PageWrapper>} />
            <Route path="/changelog" element={<Changelog />} />

            {/* Utility Tools */}
            <Route path="/qr-generator" element={<PageWrapper><QRGenerator /></PageWrapper>} />
            <Route path="/password-generator" element={<PageWrapper><PasswordGenerator /></PageWrapper>} />
            <Route path="/hash-generator" element={<PageWrapper><HashGenerator /></PageWrapper>} />
            <Route path="/base64" element={<PageWrapper><Base64 /></PageWrapper>} />
            <Route path="/uuid-generator" element={<PageWrapper><UUIDGenerator /></PageWrapper>} />
            <Route path="/color-picker" element={<PageWrapper><ColorPicker /></PageWrapper>} />
            <Route path="/lorem-ipsum" element={<PageWrapper><LoremIpsum /></PageWrapper>} />
            <Route path="/json-formatter" element={<PageWrapper><JsonFormatter /></PageWrapper>} />
            <Route path="/word-counter" element={<PageWrapper><WordCounter /></PageWrapper>} />
            <Route path="/text-case" element={<PageWrapper><TextCase /></PageWrapper>} />
            <Route path="/url-shortener" element={<PageWrapper><UrlShortener /></PageWrapper>} />
            <Route path="/e-signature" element={<PageWrapper><ESignature /></PageWrapper>} />
            <Route path="/video-downloader" element={<PageWrapper><VideoDownloader /></PageWrapper>} />

            {/* Special Tools (auth required, except Credit Card Gen) */}
            <Route path="/temp-mail" element={<PageWrapper><TempMail /></PageWrapper>} />
            <Route path="/credit-card-generator" element={<PageWrapper><CreditCardGenerator /></PageWrapper>} />
            <Route path="/ip-info" element={<PageWrapper><RequireAuth><IpInfo /></RequireAuth></PageWrapper>} />
            <Route path="/luhn-validator" element={<PageWrapper><RequireAuth><LuhnValidator /></RequireAuth></PageWrapper>} />
            <Route path="/request-encryptor" element={<PageWrapper><RequireAuth><RequestEncryptor /></RequireAuth></PageWrapper>} />
            <Route path="/identity" element={<PageWrapper><RequireAuth><IdentityGenerator /></RequireAuth></PageWrapper>} />
            <Route path="/curl-converter" element={<PageWrapper><RequireAuth><CurlConverter /></RequireAuth></PageWrapper>} />

            {/* Image Tools */}
            <Route path="/image-compressor" element={<PageWrapper><ImageCompressor /></PageWrapper>} />
            <Route path="/image-resizer" element={<PageWrapper><ImageResizer /></PageWrapper>} />

            {/* Document Tools */}
            <Route path="/pdf-studio" element={<PageWrapper><PDFStudio /></PageWrapper>} />
            <Route path="/student-slip" element={<PageWrapper><StudentSlip /></PageWrapper>} />

            <Route path="/resume-builder" element={<PageWrapper><ResumeBuilder /></PageWrapper>} />
            <Route path="/invoice-generator" element={<PageWrapper><InvoiceGenerator /></PageWrapper>} />

            {/* SEO & Developer Tools */}
            <Route path="/meta-tags" element={<PageWrapper><MetaTags /></PageWrapper>} />
            <Route path="/timestamp" element={<PageWrapper><Timestamp /></PageWrapper>} />
            <Route path="/markdown-editor" element={<PageWrapper><MarkdownEditor /></PageWrapper>} />
            <Route path="/unit-converter" element={<PageWrapper><UnitConverter /></PageWrapper>} />
            <Route path="/sitemap-generator" element={<PageWrapper><SitemapGenerator /></PageWrapper>} />
            <Route path="/url-encoder-decoder" element={<PageWrapper><UrlEncoderDecoder /></PageWrapper>} />
            <Route path="/pomodoro-timer" element={<PageWrapper><PomodoroTimer /></PageWrapper>} />
            <Route path="/ascii-art-generator" element={<PageWrapper><AsciiArtGenerator /></PageWrapper>} />
            <Route path="/text-diff" element={<PageWrapper><TextDiff /></PageWrapper>} />
            <Route path="/text-diff-side-by-side" element={<PageWrapper><TextDiffSideBySide /></PageWrapper>} />
            <Route path="/string-inspector" element={<PageWrapper><StringInspector /></PageWrapper>} />
            <Route path="/cron-editor" element={<PageWrapper><CronEditor /></PageWrapper>} />

            {/* Encryptors (auth required) */}
            <Route path="/encryptor/adyen-v1" element={<PageWrapper><RequireAuth><AdyenV1 /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/adyen-v2" element={<PageWrapper><RequireAuth><AdyenV2 /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/cybersource-v1" element={<PageWrapper><RequireAuth><CybersourceV1 /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/cybersource-v2" element={<PageWrapper><RequireAuth><CybersourceV2 /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/card-cvv" element={<PageWrapper><RequireAuth><CardCvv /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/vantiv" element={<PageWrapper><RequireAuth><Vantiv /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/clover" element={<PageWrapper><RequireAuth><Clover /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/eway" element={<PageWrapper><RequireAuth><Eway /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/cardconnect" element={<PageWrapper><RequireAuth><CardConnect /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/paytrace" element={<PageWrapper><RequireAuth><Paytrace /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/zuora" element={<PageWrapper><RequireAuth><Zuora /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/firstdata-v2" element={<PageWrapper><RequireAuth><FirstDataV2 /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/securepay" element={<PageWrapper><RequireAuth><SecurePay /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/auruspay" element={<PageWrapper><RequireAuth><Auruspay /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/oceanpayment" element={<PageWrapper><RequireAuth><Oceanpayment /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/captcha-v3" element={<PageWrapper><RequireAuth><CaptchaV3 /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/freedompay" element={<PageWrapper><RequireAuth><Freedompay /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/paysimple" element={<PageWrapper><RequireAuth><Paysimple /></RequireAuth></PageWrapper>} />
            <Route path="/encryptor/binlist" element={<PageWrapper><RequireAuth><Binlist /></RequireAuth></PageWrapper>} />

            {/* Placeholder routes - to be implemented */}
            <Route path="/vault" element={<PageWrapper><ComingSoon title="Vault" /></PageWrapper>} />
            <Route path="/vip-pricing" element={<PageWrapper><ComingSoon title="VIP Pricing" /></PageWrapper>} />

            {/* Redirect handler - tries to resolve slug before showing 404 */}
            <Route path="*" element={<PageWrapper><RedirectHandler /></PageWrapper>} />
          </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

function ComingSoon({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-4">
      <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center">
        <span className="text-2xl">🚧</span>
      </div>
      <h1 className="text-2xl font-bold">{title}</h1>
      <p className="text-muted-foreground">This tool is coming soon.</p>
      <p className="text-sm text-muted-foreground font-mono">
        <span className="text-primary">{'>'}</span> Status: In Development
      </p>
    </div>
  )
}

export default App
